import { EntityType } from '@frontend/common';
import { EventClient } from '@frontend/events/api';
import { Event } from '@frontend/events/types';
import { useEffect, useRef, useState } from 'react';
import { TransactionHistoryProps } from './transaction-history.component';

interface ViewProps {
    containerRef: React.RefObject<HTMLDivElement | null>;
    events: Event[];
    isLoading: boolean;
}

const useTransactionHistory = (props: TransactionHistoryProps): ViewProps => {
    const pageSize = props.pageSize != undefined ? props.pageSize.toString() : '50';
    const showChildren = props.showChildren ?? false;
    const [index, setIndex] = useState<string>('0');
    const [events, setEvents] = useState<Map<string, Event>>(new Map());
    const [totalCount, setTotalCount] = useState<number>(0);

    const containerRef = useRef<HTMLDivElement | null>(null);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            if (totalCount <= parseInt(pageSize)) return;
            const container = containerRef.current;
            if (container && !isLoading) {
                const { scrollTop, scrollHeight, clientHeight } = container;
                if (scrollTop + clientHeight >= scrollHeight - 100) {
                    setIndex((prev) => {
                        return (parseInt(prev) + 1).toString();
                    });
                    setIsLoading(true);
                }
            }
        };

        if (containerRef.current) {
            containerRef.current.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (containerRef.current) {
                containerRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [containerRef, isLoading]);

    const entityType = props.entityType ?? EntityType.TRANSACTION;
    const entityId = props.entityId ?? props.transactionId;

    const fetch = () => {
        if (!entityId) {
            setIsLoading(false);
            return;
        }

        EventClient.fetchEntityEvents(entityType, entityId, {
            index: index,
            size: pageSize,
            ...(showChildren ? {} : { no_children: 'true' }),
            ...props.queryParams
        }).then((res) => {
            setTotalCount(res.count);
            setEvents((prev) => {
                if (res.results.length === 0) {
                    return prev;
                }
                const newEvents = new Map(prev);
                res.results.forEach((event) => {
                    newEvents.set(event.id, event);
                });
                return newEvents;
            });
            setIsLoading(false);
        });
    };

    useEffect(() => {
        fetch();
    }, [index, showChildren, entityId, entityType]);

    return {
        events: Array.from(events.values()),
        containerRef,
        isLoading
    };
};

export default useTransactionHistory;

